import { cn } from "@/lib/utils";

interface OnboardingProgressProps {
  current: number;     // 0-indexed step
  total: number;
  labels?: string[];
}

/**
 * Step-by-step progress indicator. Dots on mobile (saves width), full
 * pill labels at sm+ — keeps the experience friendly on phones without
 * losing context on desktop.
 */
export function OnboardingProgress({ current, total, labels }: OnboardingProgressProps) {
  return (
    <div className="w-full">
      {/* Dots — mobile */}
      <div className="flex items-center justify-center gap-2 sm:hidden">
        {Array.from({ length: total }).map((_, i) => (
          <span
            key={i}
            className={cn(
              "h-2 rounded-full transition-all",
              i < current      ? "w-2 bg-primary"
              : i === current  ? "w-6 bg-primary"
              :                  "w-2 bg-border"
            )}
            aria-hidden
          />
        ))}
      </div>

      {/* Pills — desktop */}
      <ol className="hidden items-center gap-2 sm:flex">
        {Array.from({ length: total }).map((_, i) => {
          const done    = i < current;
          const active  = i === current;
          return (
            <li
              key={i}
              className={cn(
                "flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                active ? "border-brand-accent bg-brand-accentSoft text-brand-accentDark"
                       : done ? "border-brand-green/40 bg-brand-greenSoft text-brand-green"
                              : "border-border bg-background text-muted-foreground"
              )}
            >
              <span
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold",
                  active ? "bg-primary text-primary-foreground"
                         : done ? "bg-brand-green text-white"
                                : "bg-muted text-muted-foreground"
                )}
              >
                {done ? "✓" : i + 1}
              </span>
              <span className="hidden md:inline">{labels?.[i] ?? `Step ${i + 1}`}</span>
            </li>
          );
        })}
      </ol>

      <p className="mt-2 text-center text-xs text-muted-foreground sm:hidden">
        Step {current + 1} of {total}
        {labels?.[current] ? ` · ${labels[current]}` : ""}
      </p>
    </div>
  );
}
